import { HTMLAttributes } from 'react'
import { BfnStoreDataEntry } from '@/store/useBfnStore'
import { Befehlsnachricht, BfnStatus } from '@/models/Befehlsnachricht'
import useIsAbruftokenExpired from '@/features/befehlsnachrichten/hooks/useIsAbruftokenExpired'
import useOnlineDetection from '@/features/offline/hooks/useOnlineDetection'
import { Abruftoken } from '@/models/Abruftoken'
import { useHasDeadlinePassed } from '@repo/ui'
import BfnStatusInfo from './details/BfnStatusInfo'
import BfnStandortbestaetigungActions from './details/BfnStandortbestaetigungActions'
import BfnAbgerufenActions from './details/BfnAbgerufenActions'
import BfnErledigenAction from './details/BfnErledigenAction'
import BfnManuellWiderrufenAction from './details/BfnManuellWiderrufenAction'
import BfnExportAsPdfAction from './details/BfnExportAsPdfAction'
import useIsErledigenActionBlocked from '@/features/befehlsnachrichten/hooks/useIsErledigenActionBlocked'

export interface BfnDetailsSideButtonsMenuProps extends HTMLAttributes<HTMLDivElement> {
  abruftoken: Abruftoken
  befehlsnachricht: Befehlsnachricht
  bfnDataEntry: BfnStoreDataEntry
  isStandortGelesen: boolean
  'data-testid': string
}

export default function BfnDetailsSideButtonsMenu({
  abruftoken,
  befehlsnachricht,
  bfnDataEntry,
  isStandortGelesen,
  'data-testid': testIdInit
}: Readonly<BfnDetailsSideButtonsMenuProps>) {
  const isTokenExpired = useIsAbruftokenExpired(abruftoken)
  const isOnline = useOnlineDetection()
  const isErledigenActionBlocked = useIsErledigenActionBlocked(bfnDataEntry)

  const isBfnLocked = useHasDeadlinePassed(befehlsnachricht.bearbeitbarBis)

  const isShowStatusInfo =
    !isTokenExpired &&
    [
      BfnStatus.STANDORTABGLEICH_DURCH_FDL_ERFORDERLICH,
      BfnStatus.QUITTIERT
    ].includes(befehlsnachricht.status)
  const isShowStandortBestaetigenActions =
    !isTokenExpired &&
    befehlsnachricht.status ===
      BfnStatus.STANDORTBESTAETIGUNG_DURCH_TF_ERFORDERLICH
  const isShowAbgerufenActions =
    !isTokenExpired &&
    !bfnDataEntry.manuellWiderrufenAt &&
    [BfnStatus.STANDORTEINGABE_AKZEPTIERT, BfnStatus.ABGERUFEN].includes(
      befehlsnachricht.status
    )
  const isShowExportToPdfAction = !isTokenExpired

  const isShowErledigenAction =
    befehlsnachricht.status === BfnStatus.QUITTIERT &&
    !bfnDataEntry.manuellWiderrufenAt &&
    !isTokenExpired &&
    !isBfnLocked
  const isShowManuellWiderrufenAction =
    !bfnDataEntry.manuellWiderrufenAt &&
    !isTokenExpired &&
    !isBfnLocked &&
    [
      BfnStatus.STANDORTEINGABE_AKZEPTIERT,
      BfnStatus.ABGERUFEN,
      BfnStatus.QUITTIERT,
      BfnStatus.ERLEDIGT
    ].includes(befehlsnachricht.status)

  return (
    <div className="mt-2 md:mt-[18px] flex flex-col gap-1 md:w-2/6">
      {isShowStatusInfo && isOnline && (
        <BfnStatusInfo
          bfnDataEntry={bfnDataEntry}
          data-testid={`${testIdInit}-status`}
        />
      )}
      {isShowStandortBestaetigenActions && (
        <BfnStandortbestaetigungActions
          abruftoken={abruftoken}
          isStandortGelesen={!isStandortGelesen}
          data-testid={`${testIdInit}-standort-bestaetigen`}
        />
      )}
      {isShowAbgerufenActions && (
        <BfnAbgerufenActions
          abruftoken={abruftoken}
          data-testid={`${testIdInit}-abgerufen`}
        />
      )}
      {isShowErledigenAction && (
        <BfnErledigenAction
          data={bfnDataEntry}
          isBlocked={isErledigenActionBlocked}
          data-testid={`${testIdInit}-erledigen`}
        />
      )}
      {isShowManuellWiderrufenAction &&
        befehlsnachricht.status === BfnStatus.ERLEDIGT && (
          <BfnManuellWiderrufenAction
            data={bfnDataEntry}
            data-testid={`${testIdInit}-manuell-widerrufen`}
          />
        )}
      {isShowManuellWiderrufenAction &&
        befehlsnachricht.status !== BfnStatus.ERLEDIGT && (
          <BfnManuellWiderrufenAction
            data={bfnDataEntry}
            data-testid={`${testIdInit}-manuell-widerrufen`}
          />
        )}
      {isShowExportToPdfAction && (
        <BfnExportAsPdfAction
          data={bfnDataEntry}
          data-testid={`${testIdInit}-export-pdf`}
        />
      )}
    </div>
  )
}
